// src/components/Household/HouseholdPage.js
// F9 — Shared Household Budgets
// Prism Agent — UI/UX
//
// Full-page household management UI with two tabs:
//   1. My Households — list cards, create dialog
//   2. Members — per-household member list, invite form, role management

import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Tabs,
  Tab,
  Card,
  CardContent,
  CardActions,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Chip,
  IconButton,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Avatar,
  Divider,
  CircularProgress,
  Alert,
  Tooltip,
  Stack,
  Paper,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import DeleteIcon from '@mui/icons-material/Delete';
import GroupIcon from '@mui/icons-material/Group';
import HomeIcon from '@mui/icons-material/Home';
import PeopleIcon from '@mui/icons-material/People';
import api from '../../services/api';

// ─── Role colour helpers ──────────────────────────────────────────────────────

const ROLE_COLORS = {
  owner: 'error',
  admin: 'warning',
  editor: 'primary',
  viewer: 'default',
};

const ROLE_LABELS = {
  owner: 'Owner',
  admin: 'Admin',
  editor: 'Editor',
  viewer: 'Viewer',
};

// ─── Sub-components ────────────────────────────────────────────────────────────

function HouseholdCard({ household, currentUserId, onSelect, selected, onDelete }) {
  const memberCount = household.members
    ? household.members.filter((m) => m.inviteStatus === 'accepted').length
    : 0;

  const myMember = household.members
    ? household.members.find(
        (m) =>
          m.userId && m.userId._id
            ? m.userId._id === currentUserId
            : m.userId === currentUserId,
      )
    : null;

  const myRole =
    household.owner && (household.owner._id === currentUserId || household.owner === currentUserId)
      ? 'owner'
      : myMember
      ? myMember.role
      : 'viewer';

  return (
    <Card
      variant={selected ? 'elevation' : 'outlined'}
      elevation={selected ? 6 : 1}
      sx={{
        cursor: 'pointer',
        border: selected ? '2px solid' : '1px solid',
        borderColor: selected ? 'primary.main' : 'divider',
        transition: 'all 0.2s',
        '&:hover': { boxShadow: 4 },
      }}
      onClick={() => onSelect(household)}
    >
      <CardContent sx={{ pb: 1 }}>
        <Stack direction="row" alignItems="center" spacing={1} mb={1}>
          <HomeIcon color="primary" />
          <Typography variant="h6" fontWeight={600} noWrap flex={1}>
            {household.name}
          </Typography>
          <Chip
            label={ROLE_LABELS[myRole] || myRole}
            color={ROLE_COLORS[myRole] || 'default'}
            size="small"
          />
        </Stack>

        <Typography variant="body2" color="text.secondary">
          Owner:{' '}
          <strong>
            {household.owner
              ? household.owner.name || household.owner.email || 'Unknown'
              : 'Unknown'}
          </strong>
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {memberCount} accepted member{memberCount !== 1 ? 's' : ''}
        </Typography>
      </CardContent>
      <CardActions sx={{ pt: 0, justifyContent: 'flex-end' }}>
        {myRole === 'owner' && (
          <Tooltip title="Delete household">
            <IconButton
              size="small"
              color="error"
              onClick={(e) => {
                e.stopPropagation();
                onDelete(household);
              }}
            >
              <DeleteIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        )}
        <Button size="small" onClick={() => onSelect(household)}>
          {selected ? 'Selected' : 'Manage Members'}
        </Button>
      </CardActions>
    </Card>
  );
}

function MemberRow({ member, currentUserId, isAdminOrOwner, onRoleChange, onRemove }) {
  const userId =
    member.userId && typeof member.userId === 'object'
      ? member.userId._id
      : member.userId;

  const isSelf = userId === currentUserId;
  const displayName =
    member.userId && member.userId.name
      ? member.userId.name
      : member.inviteEmail || 'Unknown';
  const displayEmail =
    member.userId && member.userId.email ? member.userId.email : member.inviteEmail || '';
  const avatarSrc = member.userId && member.userId.avatar ? member.userId.avatar : null;
  const initials = displayName.charAt(0).toUpperCase();

  return (
    <Stack
      direction="row"
      alignItems="center"
      spacing={2}
      py={1}
      px={1}
      sx={{ '&:hover': { bgcolor: 'action.hover' }, borderRadius: 1 }}
    >
      <Avatar src={avatarSrc} sx={{ width: 36, height: 36, fontSize: 14 }}>
        {initials}
      </Avatar>
      <Box flex={1} minWidth={0}>
        <Typography variant="body2" fontWeight={600} noWrap>
          {displayName}
          {isSelf && (
            <Chip label="You" size="small" sx={{ ml: 1, fontSize: 10 }} color="primary" />
          )}
        </Typography>
        <Typography variant="caption" color="text.secondary" noWrap>
          {displayEmail}
        </Typography>
      </Box>

      <Chip
        label={member.inviteStatus === 'pending' ? 'Pending' : ROLE_LABELS[member.role] || member.role}
        color={member.inviteStatus === 'pending' ? 'default' : ROLE_COLORS[member.role] || 'default'}
        size="small"
        variant={member.inviteStatus === 'pending' ? 'outlined' : 'filled'}
      />

      {isAdminOrOwner && member.inviteStatus === 'accepted' && (
        <FormControl size="small" sx={{ minWidth: 100 }}>
          <Select
            value={member.role}
            onChange={(e) => onRoleChange(member._id, e.target.value)}
            variant="outlined"
            sx={{ fontSize: 13 }}
          >
            <MenuItem value="viewer">Viewer</MenuItem>
            <MenuItem value="editor">Editor</MenuItem>
            <MenuItem value="admin">Admin</MenuItem>
          </Select>
        </FormControl>
      )}

      {(isAdminOrOwner || isSelf) && (
        <Tooltip title={isSelf ? 'Leave household' : 'Remove member'}>
          <IconButton size="small" color="error" onClick={() => onRemove(member._id)}>
            <DeleteIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      )}
    </Stack>
  );
}

// ─── Main Page Component ───────────────────────────────────────────────────────

export default function HouseholdPage() {
  const [tabValue, setTabValue] = useState(0);
  const [households, setHouseholds] = useState([]);
  const [selectedHousehold, setSelectedHousehold] = useState(null);
  const [loading, setLoading] = useState(true);
  const [membersLoading, setMembersLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // Create dialog
  const [createOpen, setCreateOpen] = useState(false);
  const [newName, setNewName] = useState('');
  const [creating, setCreating] = useState(false);

  // Invite
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteRole, setInviteRole] = useState('viewer');
  const [inviting, setInviting] = useState(false);

  // Delete confirm dialog
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);

  // Current user
  const [currentUserId, setCurrentUserId] = useState(null);

  // ── Fetch current user ──────────────────────────────────────────────────
  useEffect(() => {
    api
      .get('/auth/me')
      .then((res) => {
        const u = res.data.user || res.data;
        setCurrentUserId(u._id || u.id);
      })
      .catch(() => {});
  }, []);

  // ── Load households ─────────────────────────────────────────────────────
  const loadHouseholds = useCallback(async () => {
    try {
      setLoading(true);
      const res = await api.get('/households');
      setHouseholds(res.data.households || []);
    } catch (err) {
      setError('Failed to load households');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadHouseholds();
  }, [loadHouseholds]);

  // ── Load single household (with member details) ─────────────────────────
  const loadHousehold = useCallback(async (householdId) => {
    try {
      setMembersLoading(true);
      const res = await api.get(`/households/${householdId}`);
      setSelectedHousehold(res.data.household);
    } catch (err) {
      setError('Failed to load household details');
    } finally {
      setMembersLoading(false);
    }
  }, []);

  const handleSelectHousehold = (household) => {
    setTabValue(1);
    setSelectedHousehold(household);
    loadHousehold(household._id);
    setSuccess('');
    setError('');
  };

  // ── Create household ────────────────────────────────────────────────────
  const handleCreate = async () => {
    if (!newName.trim()) return;
    setCreating(true);
    try {
      await api.post('/households', { name: newName.trim() });
      setSuccess('Household created!');
      setCreateOpen(false);
      setNewName('');
      loadHouseholds();
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to create household');
    } finally {
      setCreating(false);
    }
  };

  // ── Invite member ───────────────────────────────────────────────────────
  const handleInvite = async () => {
    if (!inviteEmail.trim() || !selectedHousehold) return;
    setInviting(true);
    setError('');
    try {
      await api.post(`/households/${selectedHousehold._id}/invite`, {
        email: inviteEmail.trim(),
        role: inviteRole,
      });
      setSuccess(`Invite sent to ${inviteEmail.trim()}`);
      setInviteEmail('');
      setInviteRole('viewer');
      loadHousehold(selectedHousehold._id);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to send invite');
    } finally {
      setInviting(false);
    }
  };

  // ── Change member role ──────────────────────────────────────────────────
  const handleRoleChange = async (memberId, role) => {
    if (!selectedHousehold) return;
    try {
      await api.patch(`/households/${selectedHousehold._id}/members/${memberId}`, { role });
      setSuccess('Role updated');
      loadHousehold(selectedHousehold._id);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to update role');
    }
  };

  // ── Remove member ───────────────────────────────────────────────────────
  const handleRemoveMember = async (memberId) => {
    if (!selectedHousehold) return;
    try {
      await api.delete(`/households/${selectedHousehold._id}/members/${memberId}`);
      setSuccess('Member removed');
      loadHousehold(selectedHousehold._id);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to remove member');
    }
  };

  // ── Delete household ────────────────────────────────────────────────────
  const handleDeleteHousehold = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      await api.delete(`/households/${deleteTarget._id}`);
      setSuccess('Household deleted');
      setDeleteTarget(null);
      if (selectedHousehold && selectedHousehold._id === deleteTarget._id) {
        setSelectedHousehold(null);
        setTabValue(0);
      }
      loadHouseholds();
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to delete household');
    } finally {
      setDeleting(false);
    }
  };

  // ── Helpers ─────────────────────────────────────────────────────────────
  const myRoleInSelected = selectedHousehold
    ? selectedHousehold.owner &&
      (selectedHousehold.owner._id === currentUserId ||
        selectedHousehold.owner === currentUserId)
      ? 'owner'
      : (selectedHousehold.members || []).find(
          (m) =>
            m.userId &&
            (m.userId._id === currentUserId || m.userId === currentUserId) &&
            m.inviteStatus === 'accepted',
        )?.role || 'viewer'
    : null;

  const isAdminOrOwner =
    myRoleInSelected === 'owner' || myRoleInSelected === 'admin';

  return (
    <Box sx={{ p: { xs: 2, md: 4 }, maxWidth: 900, mx: 'auto' }}>
      {/* Header */}
      <Stack direction="row" alignItems="center" justifyContent="space-between" mb={3}>
        <Stack direction="row" alignItems="center" spacing={1}>
          <GroupIcon color="primary" sx={{ fontSize: 32 }} />
          <Typography variant="h4" fontWeight={700}>
            Household Budgets
          </Typography>
        </Stack>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => setCreateOpen(true)}
          size="medium"
        >
          New Household
        </Button>
      </Stack>

      {/* Alerts */}
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}
      {success && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess('')}>
          {success}
        </Alert>
      )}

      {/* Tabs */}
      <Tabs value={tabValue} onChange={(_, v) => setTabValue(v)} sx={{ mb: 3 }}>
        <Tab icon={<HomeIcon />} iconPosition="start" label="My Households" />
        <Tab
          icon={<PeopleIcon />}
          iconPosition="start"
          label={selectedHousehold ? `Members — ${selectedHousehold.name}` : 'Members'}
          disabled={!selectedHousehold}
        />
      </Tabs>

      {/* ── Tab 0: My Households ─────────────────────────────────────────── */}
      {tabValue === 0 && (
        <Box>
          {loading ? (
            <Box display="flex" justifyContent="center" py={6}>
              <CircularProgress />
            </Box>
          ) : households.length === 0 ? (
            <Paper variant="outlined" sx={{ p: 6, textAlign: 'center' }}>
              <HomeIcon sx={{ fontSize: 56, color: 'text.disabled', mb: 2 }} />
              <Typography variant="h6" color="text.secondary" gutterBottom>
                No households yet
              </Typography>
              <Typography variant="body2" color="text.secondary" mb={3}>
                Create a household to start sharing budgets with family or roommates.
              </Typography>
              <Button variant="contained" startIcon={<AddIcon />} onClick={() => setCreateOpen(true)}>
                Create First Household
              </Button>
            </Paper>
          ) : (
            <Box
              display="grid"
              gridTemplateColumns={{ xs: '1fr', sm: '1fr 1fr', md: '1fr 1fr 1fr' }}
              gap={2}
            >
              {households.map((h) => (
                <HouseholdCard
                  key={h._id}
                  household={h}
                  currentUserId={currentUserId}
                  selected={selectedHousehold && selectedHousehold._id === h._id}
                  onSelect={handleSelectHousehold}
                  onDelete={setDeleteTarget}
                />
              ))}
            </Box>
          )}
        </Box>
      )}

      {/* ── Tab 1: Members ──────────────────────────────────────────────── */}
      {tabValue === 1 && selectedHousehold && (
        <Box>
          {/* Invite form — admins/owners only */}
          {isAdminOrOwner && (
            <Paper variant="outlined" sx={{ p: 2, mb: 3 }}>
              <Typography variant="subtitle1" fontWeight={600} mb={2}>
                <PersonAddIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
                Invite a Member
              </Typography>
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                <TextField
                  label="Email address"
                  type="email"
                  value={inviteEmail}
                  onChange={(e) => setInviteEmail(e.target.value)}
                  size="small"
                  fullWidth
                  onKeyDown={(e) => e.key === 'Enter' && handleInvite()}
                />
                <FormControl size="small" sx={{ minWidth: 120 }}>
                  <InputLabel>Role</InputLabel>
                  <Select
                    value={inviteRole}
                    label="Role"
                    onChange={(e) => setInviteRole(e.target.value)}
                  >
                    <MenuItem value="viewer">Viewer</MenuItem>
                    <MenuItem value="editor">Editor</MenuItem>
                    <MenuItem value="admin">Admin</MenuItem>
                  </Select>
                </FormControl>
                <Button
                  variant="contained"
                  onClick={handleInvite}
                  disabled={inviting || !inviteEmail.trim()}
                  startIcon={inviting ? <CircularProgress size={16} /> : <PersonAddIcon />}
                  sx={{ whiteSpace: 'nowrap' }}
                >
                  Send Invite
                </Button>
              </Stack>
            </Paper>
          )}

          {/* Member list */}
          {membersLoading ? (
            <Box display="flex" justifyContent="center" py={4}>
              <CircularProgress />
            </Box>
          ) : (
            <Paper variant="outlined" sx={{ p: 1 }}>
              <Typography variant="subtitle2" color="text.secondary" px={1} py={1}>
                {(selectedHousehold.members || []).length} member
                {(selectedHousehold.members || []).length !== 1 ? 's' : ''}
              </Typography>
              <Divider />
              {(selectedHousehold.members || []).map((member, idx) => (
                <React.Fragment key={member._id}>
                  <MemberRow
                    member={member}
                    currentUserId={currentUserId}
                    isAdminOrOwner={isAdminOrOwner}
                    onRoleChange={handleRoleChange}
                    onRemove={handleRemoveMember}
                  />
                  {idx < selectedHousehold.members.length - 1 && <Divider />}
                </React.Fragment>
              ))}
              {(selectedHousehold.members || []).length === 0 && (
                <Typography variant="body2" color="text.secondary" p={3} textAlign="center">
                  No members yet. Invite someone to get started.
                </Typography>
              )}
            </Paper>
          )}
        </Box>
      )}

      {/* ── Create Household Dialog ─────────────────────────────────────── */}
      <Dialog
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Create New Household</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            label="Household name"
            placeholder="e.g. Smith Family, Apartment 4B"
            value={newName}
            onChange={(e) => setNewName(e.target.value)}
            fullWidth
            sx={{ mt: 1 }}
            onKeyDown={(e) => e.key === 'Enter' && handleCreate()}
            inputProps={{ maxLength: 100 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreateOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleCreate}
            disabled={creating || !newName.trim()}
          >
            {creating ? 'Creating…' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ── Delete Household Confirm Dialog ────────────────────────────── */}
      <Dialog
        open={Boolean(deleteTarget)}
        onClose={() => setDeleteTarget(null)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Delete Household?</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete{' '}
            <strong>{deleteTarget?.name}</strong>? This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteTarget(null)}>Cancel</Button>
          <Button
            variant="contained"
            color="error"
            onClick={handleDeleteHousehold}
            disabled={deleting}
          >
            {deleting ? 'Deleting…' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
